import * as React from 'react';
import equal from 'fast-deep-equal/react';
import styles from './Footer.scss';
import classNames from 'classnames';
import { interpolate, t } from '~/utils/localization';
import { isChina, settings } from '~/utils/settings';
import { initCookieSettingsLink } from '~/utils/onetrust';
import { arrayToObjectByKey, isInGameBrowser, isMobileOrTabletWindow } from '~/utils/utils';
import { CATEGORIES, IFRAME_CATEGORIES } from '~/const';
import { isHiddenFooterForCategory } from '~/utils/category';
import { RootState, useAppSelector } from '~/Store';
import { createFooter, getLegalLinksFallback, getLegalLinks, LegalLinkNames, initDictionaryCatalog } from '@wg/wows-common-footer/main';
import type { IFooterProps, Realm } from '@wg/wows-common-footer/types';

const stateSelector = (state: RootState) => {
    return {
        currentPage: state.app.currentPage,
        categories: state.app.categories,
        isFullscreen: state.app.isFullscreen,
    };
};

export function getCopyrightText(): string {
    const currentYear = new Date().getUTCFullYear();
    return interpolate(t('Copyright © {years} Wargaming.net Все права защищены.'), {
        years: `2012–${currentYear}`,
    });
}

const Footer: React.FC<unknown> = (): React.ReactElement => {
    const appState = useAppSelector(stateSelector, equal);
    const [isVisible, setVisibility] = React.useState(true);

    React.useEffect(() => {
        if (isInGameBrowser) {
            return;
        }

        async function initFooter() {
            await initDictionaryCatalog({ language: settings.languageCode, dictionaryCatalog: window.i18n.catalog });

            const linksConfig = getLegalLinks({ language: settings.languageCode, realm: settings.realm as Realm });
            delete linksConfig[LegalLinkNames.PARTNERS];
            const legalLinks = getLegalLinksFallback({ language: settings.languageCode, realm: settings.realm as Realm, linksConfig });

            const realmConfig = arrayToObjectByKey(settings.realmsConfig, 'realm');

            const options: IFooterProps = {
                selector: '#footer-container',
                realm: settings.realm as Realm,
                language: settings.languageCode,
                version: settings.version,
                dictionaryCatalog: window.i18n.catalog,
                rootUrl: null,
                legalLinks,
                onLanguageChange: ({ realm, language }) => {
                    location.href = realmConfig[realm].changeLanguageUrl.replace('{}', language).replace('{}', location.pathname);
                },
                onClickByLink: (event: MouseEvent) => {
                    event.preventDefault();
                    const target = event.currentTarget as HTMLLinkElement;
                    if (target.id !== 'ot-sdk-btn') {
                        window.open(target.href, '_blank');
                    }
                },
            };

            if (isChina()) {
                options.realms = [];
            }

            createFooter(options);

            if (window.OneTrust) {
                window.OneTrust.initializeCookiePolicyHtml();
                initCookieSettingsLink();
            }
        }

        initFooter();
    }, []);

    React.useEffect(() => {
        if (IFRAME_CATEGORIES.includes(appState.currentPage?.name)) {
            setVisibility(false);
            return;
        }

        if (!appState.currentPage?.isBundlePage && isHiddenFooterForCategory(appState.categories[appState.currentPage?.name])) {
            setVisibility(false);
            return;
        }

        if (isMobileOrTabletWindow) {
            if (
                appState.currentPage?.isBundlePage ||
                appState.currentPage?.name === CATEGORIES.MENU ||
                appState.currentPage?.name === CATEGORIES.WALLET ||
                appState.currentPage?.name === CATEGORIES.COUPONS
            ) {
                setVisibility(false);
                return;
            }
        }

        if (!isVisible) {
            setVisibility(true);
        }
    }, [appState.currentPage?.name]);

    return (
        <div
            id={'footer-container'}
            className={classNames(styles.wrapper, {
                [styles.hidden]: !isVisible,
            })}
        />
    );
};

export default Footer;
